// Two-way texting (owner request, PJ, 2026-10-03; migration 0024). Wire types shared by the Worker and the web app.
//
//   GET  /api/sms/threads?scope=mine|all&unread=1  -> SmsThreadItem[]   (unread first, then newest; max 200)
//   GET  /api/sms/threads/:id                     -> SmsThreadDetail   (marks inbound read for the person it is for)
//   GET  /api/sms/business/:businessId            -> SmsBusinessView   (the dialer's Text panel)
//   GET  /api/sms/unread                          -> SmsUnread         (nav badge, polled every 20 s)
//   GET  /api/sms/new-text?to=&businessId=        -> SmsNewTextView    (the Texts page's New text composer, 2026-10-04)
//   POST /api/sms/send            SmsSendRequest  -> SmsSendResponse   (409 late_hour: resend with confirmLate: true;
//                                                                        409 consent_needed: tick the consent box;
//                                                                        409 sms_blocked: a hard block, in one sentence;
//                                                                        403: a conversation with that number the
//                                                                        sender can't see, or a lead not theirs;
//                                                                        429 sms_rate_limited: the per-user send cap)
//   POST /api/sms/threads/:id/link   { businessId }  -> SmsThreadDetail
//   POST /api/sms/threads/:id/assign { userId|null } -> SmsThreadDetail (admins)
//   POST /api/sms/threads/:id/assist       SmsAssistRequest -> SmsAssistResponse (AI draft / summary, 2026-10-06;
//                                            never sends; 409 sms_blocked: nothing can be sent there, so no draft;
//                                            409 nothing_to_summarize; 422 draft_rejected: the draft broke the rules
//                                            twice; 429 rate_limited: the daily quota; 503 ai_not_configured)
//   POST /api/sms/business/:businessId/assist SmsAssistRequest (+ to) -> SmsAssistResponse (the first text to a lead
//                                            with no conversation yet: the call screen's Text panel, New text)
//   Twilio: POST /twilio/sms (each number's SMS URL), POST /twilio/sms-status (per-message StatusCallback).
import type { Country, ISODate } from './api';

export type SmsDirection = 'in' | 'out';
export type SmsStatus = 'received' | 'queued' | 'sent' | 'delivered' | 'undelivered' | 'failed';
/** How a thread got its business: their number IS the business's number (or a number only that business used with us:
 *  another conversation, a booking mobile, an inbound call); our number called exactly one business in the 15 minutes
 *  before the text; someone linked it by hand; or we texted first from the lead. */
export type SmsLinkSource = 'phone_match' | 'recent_call' | 'manual' | 'outbound';

export const SMS_MAX_CHARS = 1600;        // Twilio's limit for one message Body
export const SMS_QUIET_START_HOUR = 21;    // 9 PM their time: sending later asks "Send anyway?" (a recommendation)
export const SMS_QUIET_END_HOUR = 8;       // 8 AM their time
/** The one-click line the composer offers on Canadian numbers (recommended, not enforced). */
export const SMS_CANADA_OPT_OUT_LINE = 'Reply STOP to opt out.';
/** Per-user send cap (a runaway-loop and spam guard): 429 sms_rate_limited above these. */
export const SMS_SEND_CAP_10_MIN = 60;
export const SMS_SEND_CAP_DAY = 500;
/** An inbound text that is exactly one of these words (any case, trailing punctuation ignored) is a request to stop:
 *  the number is never texted again from any of our numbers until it sends a START word. */
export const SMS_STOP_WORDS: readonly string[] = ['STOP', 'STOPALL', 'UNSUBSCRIBE', 'CANCEL', 'END', 'QUIT', 'OPTOUT', 'REVOKE'];
export const SMS_START_WORDS: readonly string[] = ['START', 'UNSTOP', 'YES'];
/** A plain request to stop in their own words (review 2026-10-12: "Stop texting me", "Don't call me again", "Remove me",
 *  "No more texts" must stop us too; consent can be withdrawn by any reasonable means, and the FCC's 2025 rule counts a
 *  reply using stop, quit, end, revoke, opt out, cancel or unsubscribe). Round 1 of the second review (2026-10-12) took out
 *  false opt-outs; round 2 (adversarial review of 9cfede4) found both kinds still:
 *  - missed: a stop keyword in a sentence ("Who is this? STOP", "STOP. Who is this", "Stop, wrong number": a keyword that
 *    is a clause of its own, stopKeyword), "Do not call or text me", "Do not call", "I said stop", "opt me out", "unsub",
 *    "Please remove", "Stop spamming me", "delete my number", "We don't want any more texts", "How do I unsubscribe";
 *  - false: an interested reply ("No more calls going to voicemail would be huge", "Please stop the leak first lol"), a
 *    meeting ("Can you take me off the 2pm and put me at 3?"), a time window ("Don't text me so early lol", "No more texts
 *    after 6"), another number ("Don't call this number, call 905-555-1234"), someone else ("My guys don't text").
 *  "Don't text" is a request only at the start of a clause, said to us ("Don't text me", "Dont text again"), never "I
 *  don't text and drive" or "my guys don't text"; a request about calls only stops nothing when the same text asks for
 *  another channel, number or time (CALLS_REDIRECT_RE). "Don't text me, call me" is a stop for texts (they asked not to be
 *  texted; whether to call is the setter's call). Read on smsWithoutCannedStop(body).
 *  Final review (adversarial review of 032aa5a): do-not-call list wording ("Put me on your do not call list", "Add me to
 *  your DNC list"), "Get me off your list", "I want off your list", "I'd like to be removed", "I asked you to stop", "Stop -
 *  wrong number" and "Who gave you my number? Remove it." are stops; a business's opt-out instruction with the keyword in
 *  quotes, "anytime" or "2" ("Reply 'STOP' to unsubscribe", "You can reply STOP anytime to unsubscribe", "Reply STOP 2
 *  unsubscribe"), "Unsubscribe from what?" and "When does it stop ringing and the AI pick up?" are not. */
/** "Don't" at the start of a clause (or after please / just / and / so / ok): said to us, not "I don't text and drive". */
const DONT = String.raw`(?:^|[.!?,;:\n]\s*|\b(?:please|pls|plz|just|and|so|ok|okay|then|now|but|also|seriously|again)\s+)do(?:n'?t| not|nt) (?:ever )?`;
const STOP_TEXTS_RES: readonly RegExp[] = [
  // stop texting / contacting / spamming (also "stop calling and texting me", "stop with the texts")
  /\bstop (?:(?:calling|call) (?:and|&|or|n) )?(?:texting|txting|messaging|msging|contacting|bothering|spamming|harassing|bugging|sending (?:me |us )?(?:texts|messages|these|this|stuff))\b/i,
  /\bstop with (?:the |these |your |all the )?(?:texts|messages|spam)\b/i,
  /\bquit (?:texting|txting|messaging|msging|contacting|bothering|spamming|harassing|bugging)\b/i,
  // "please stop" at the end or before texting / it / now; not "please stop by", "please stop the leak first", "please stop calls going to vm"
  /\b(?:please|pls|plz) stop\b(?=\s*(?:$|[.!,;?)]|(?:texting|txting|messaging|msging|contacting|it|now|already|thanks|thx|ty|asap|this)\b))/i,
  // not "Can you remove my number from my Google listing?" (ship check of 09da0c7: a website/SEO request, not a stop)
  /\b(?:remove|delete) (?:me|my number|this number|us|my (?:info|contact|details))\b(?!\s+(?:from|off|on)\s+(?:my|the|our|that)\s+(?:old\s+)?(?:google|listing|site|website|web ?site|profile|page|gbp|map|maps)\b)/i,
  /^\W*(?:please |pls |plz )?remove(?: (?:me|us|please|pls))?\W*$/i,
  // "Who gave you my number? Remove it."
  /\bmy (?:number|cell|phone)\b[^\n]{0,60}?\b(?:remove|delete|erase|lose) (?:it|that|this|me)\b/i,
  // "I'd like to be removed", "I want to be taken off", "asked to be removed"
  /\b(?:like|want|wish|need|wanna|asking|asked|ask) (?:to )?(?:be|get) (?:removed|taken off|unsubscribed|opted out)\b/i,
  // "take me off" / "get me off" your list / it / the end; not "take me off the 2pm"
  /\b(?:take|get) (?:me|my number|this number|us) off\b(?=\s*(?:$|[.!,;?]|(?:of )?(?:your|ur|the|this|that|ya|all|ur|y'?all'?s) (?:[\w-]+ )?list\b|(?:of )?it\b|(?:please|pls|plz|now|thanks|asap)\b))/i,
  // "I want off your list", "I want off"
  /\b(?:want|wanna|need|would like|'d like|like) (?:to (?:be|get) )?off (?:of )?(?:your|ur|the|this|that|ya|all) (?:[\w-]+ )?list\b|\b(?:want|wanna) off\W*$/i,
  // do-not-call list wording: "Put me on your do not call list", "Add me to your DNC list", "Please add this number to
  // your do not call list", "Put me down as do not contact", and the bare list ("Do not call list please")
  /\b(?:put|add|place|keep|list) (?:me|us|this number|my number|this phone|my phone)(?: down)? (?:on|to|in|as) (?:your |the |ur |a )?(?:do[- ]?not[- ]?(?:call|contact|text)|dnc|no[- ]?(?:call|contact))\b/i,
  // not a product question or their own list ("How does it handle a do not call list?", "We use a DNC list for our own
  // marketing"; ship check of 09da0c7)
  /(?<!\b(?:how|does|do|can|will|would|we use|we have|we keep|our own)\b[^.?!\n]{0,30})\b(?:do[- ]?not[- ]?(?:call|contact|text)|dnc|no[- ]?call) list\b(?![^.!?\n]{0,40}(?:\?|\bfor our own\b))/i,
  // "Unsubscribe", "Please unsubscribe me", "unsubscribe this number"; not "Unsubscribe from what? I never got anything"
  // (a business's "Reply STOP to unsubscribe" is taken out before: smsWithoutCannedStop)
  /^\W*(?:(?:please|pls|plz|just|ok|okay|and)\s+)?unsub(?:scribe)?\b(?!\s+(?:from\s+)?what\b)/i,
  /\bunsub(?:scribe)?\s+(?:me|us|this (?:number|phone|line)|my (?:number|phone))\b/i,
  // Review of 0f5db92: the start-only rule above missed "Not interested unsubscribe", "Stop and unsubscribe", "Kindly
  // unsubscribe", "Where do I unsubscribe", "I unsubscribe". Anywhere in the text, except "Unsubscribe from what?", a
  // link / button / page they mention, and their own customers ("can callers unsubscribe?"). A business's "Reply STOP to
  // unsubscribe" is taken out before (smsWithoutCannedStop).
  // Ship check of 09da0c7: not a business's own line ("Unsubscribe anytime by replying STOP", "Unsubscribe with STOP"),
  // and not a product question ("Does it handle unsubscribe requests?", "Can customers still unsubscribe?", "What do you
  // mean unsubscribe"); "Where do I unsubscribe" (I / we) is still a stop.
  /(?<!\b(?:customers?|callers?|clients?|people|users?|they|patients?)\s+(?:can\s+|could\s+|will\s+)?)(?<!\b(?:does|do|can|will|would|how)\s+(?!i\b|we\b)\w+(?:\s+\w+){0,3}\s+)\bunsub(?:scribe)?\b(?!\s+(?:from\s+)?(?:what|which)\b)(?![^.!?\n]{0,15}\b(?:link|button|page)\b)(?!\s+(?:at\s+)?any ?time\b|\s+by\b|\s+with\b)/i,
  // A stop keyword as the last word after a no, a wrong number, who-is-this, "and" or a please ("not interested stop",
  // "wrong number stop", "Who is this stop", "pls stop and unsub").
  /\b(?:interested|number|this|and|but|please|pls|plz|kindly|thanks|thx)\s+(?:stop|unsub(?:scribe)?)\W*$/i,
  // Hostile shut-downs (review of 0f5db92: "Reported as spam", "Blocked", "Spam", "Scam", "No soliciting" got a pitch).
  // ("Scam?" / "Spam?" is a question, not a stop: ship check of 09da0c7)
  /^[\s.!]*(?:spam+|scam+|blocked|reported|reporting (?:you|this|this number))[\s.!]*$/i,
  // "Don't send me any more of these", "Mark me as do not contact" (ship check of 09da0c7)
  /\bdo(?:n'?t| not|nt) send (?:me |us )?(?:any ?more|anything|these|this stuff|more)\b|\bmark (?:me|us|this number) (?:as )?(?:do[- ]?not[- ]?(?:contact|call|text)|dnc)\b/i,
  /\breport(?:ed|ing)? (?:(?:you|this|this number|it|u) )?(?:as|for) spam\b|\bno solicit\w*\b|\b(?:i'?m |i am |i'?ll |gonna |going to )?block(?:ed|ing)? (?:you|this number|u|your number)\b/i,
  /\bopt (?:me|us|this number) out\b/i,
  // "Don't text me", "Do not contact us", "Dont text this number": said to us, and not a time window ("after 6", "so early")
  new RegExp(`${DONT}(?:text|txt|message|msg|contact|sms) (?:me|us|this (?:number|phone|line|cell)|here)\\b(?! (?:before|after|until|till|til|at|on|during|between|past|while|when|so|this|that|too|in the|on the|early|late|yet)\\b)`, 'i'),
  // "Dont text again", "Do not contact.", "don't text anymore": the bare verb, then only again / anymore / the end
  new RegExp(`${DONT}(?:text|txt|message|msg|contact)(?: (?:me|us))?(?: (?:again|anymore|any more|ever))?\\s*(?:[.!,;]|$)`, 'i'),
  // "Do not call or text me", "Don't ever call or text me again", "dont call or text this number again" (not "... before 9")
  new RegExp(`${DONT}(?:call|text|txt|contact|message|msg) (?:or|and|&|n|/) (?:call|text|txt|contact|message|msg)\\b(?![^.!?\\n]*\\b(?:before|after|until|till|during|between)\\b)`, 'i'),
  /\bnever (?:text|txt|contact|message) (?:me|us|this number|here)\b(?! back\b)/i,
  // "no more texts", not a time window ("no more texts after 6 please")
  /\bno more (?:texts?|txts?|messages?|msgs?)\b(?! (?:after|before|until|till|at|on|during|between|past|so|this|today|tonight)\b)/i,
  /\b(?:don'?t|do not|dont) want (?:any more|anymore|any|your|these|this|more|ur) (?:texts?|txts?|messages?|msgs?|spam)\b/i,
  /\blose (?:this|my) number\b/i,
  // said to us (start of a clause), not "Customers leave me alone on weekends lol" (ship check of 09da0c7)
  /(?:^|[.!?,;:\n]\s*)(?:(?:please|pls|just|now|so|ok|okay|and|then|seriously|can you|could you|would you)\s+)?(?:just\s+)?leave (?:me|us) alone\b/i,
  // only said to us, at the start of a clause (ship check of 09da0c7: "Yeah calls get lost when I'm under a sink" and
  // "does it go away after hours?" are warm replies, not stops)
  /(?:^|[.!?,;:\n]\s*)(?:(?:please|pls|just|now|so|ok|okay|and|then|seriously)\s+)?(?:go away|get lost|f+ off|fuck off|piss off|eff off|bugger off)\b/i,
  /\b(?:i |already |i already )?(?:said|(?:told|asked) (?:you|u|ya|y'?all)(?: guys)?(?: to)?) stop\b/i,
  /\bi (?:already )?(?:text(?:ed)?|sent|replied(?: with)?|typed) stop\b/i,
  /\b(?:this is|that'?s|this'?s|it'?s) (?:just |total |pure )?spam\b/i,
  // "Do not call" / "DO NOT CALL" as the whole text
  /^\W*(?:please\s+)?do(?:n'?t| not|nt) (?:ever )?call(?: (?:me|us|here|this number|again|anymore|ever|please))*\W*$/i,
];
/** A request about calls only ("Stop calling me", "Don't call me again", "No more calls"): a stop too, unless the same
 *  text asks for another channel, number or time (CALLS_REDIRECT_RE). "No more calls" only with nothing after it but
 *  please / thanks / from you: "No more calls going to voicemail would be huge" is a buying signal. */
const STOP_CALLS_RES: readonly RegExp[] = [
  // "stop ringing" only at us ("stop ringing me"): "When does it stop ringing and the AI pick up?" is a buying question
  /\bstop (?:calling|phoning)\b|\bstop ringing (?:me|us|my (?:phone|cell|number|line)|this (?:number|phone|line|cell))\b/i,
  /\bquit (?:calling|ringing|phoning)\b/i,
  /\bdo(?:n'?t| not|nt) (?:ever )?call (?:me |us |here |this (?:number|line|phone) )?(?:again|anymore|any more|ever)\b/i,
  /\bdo(?:n'?t| not|nt) (?:ever )?call (?:this|my) (?:number|line|phone)\b/i,
  /\bnever call (?:me|us|this number|here)\b(?! back\b)/i,
  /\bno more (?:calls?|phone calls?)\b(?=\s*(?:$|[.!,;]|(?:please|pls|thanks|thank you|thx|ever|again|period|from (?:you|u|y'?all|this number|your (?:company|number)))\b))/i,
];
/** Another channel, number or time in the same text: "text me", "text is fine", "call the office", "call my cell",
 *  "instead", "until Monday", "today", "during work hours", "after 5", another phone number. */
const CALLS_REDIRECT_RE = /\b(?:text|txt|message|email) (?:me|us)\b(?! (?:again|anymore|any more)\b)|\btext(?:ing)?(?: is|'s| works)? (?:fine|ok|okay|better|good|best)\b|\b(?:just|only) (?:text|email)\b|\bcall (?:the|my|our|his|her) (?:office|shop|cell|mobile|main|work|other|business|landline|house|home)\b|\b(?:call|reach) (?:me|us) (?:at|on|later|tomorrow|after|tonight|next|in the)\b|\binstead\b|\b(?:until|till|til|today|tonight|during|between)\b|\bthis (?:week|morning|afternoon|evening)\b|\bright now\b|\b(?:before|after) \d|\(?\b\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/i;
/** Their text without a business's canned opt-out instruction ("Reply STOP to opt out", "To unsubscribe, reply STOP",
 *  "Text STOP to unsubscribe", "Reply HELP for help, STOP to cancel", "Opt out? Reply STOP."): their own auto text is not a
 *  request to stop. A person's "I text stop and you keep texting me" is not an instruction and stays. The keyword may sit
 *  in quotes ("Reply 'STOP' to unsubscribe", curly or straight), be followed by "anytime" / "at any time" ("You can reply
 *  STOP anytime to unsubscribe"), and "2" may stand for "to" ("Reply STOP 2 unsubscribe"; final review of 032aa5a). */
const CANNED_KW = String.raw`["']?(?:stop ?all|stop|end|quit|unsubscribe|cancel|opt ?out)["']?`;
const CANNED_ANYTIME = String.raw`(?:\s+(?:at\s+)?any\s?time)?`;
const CANNED_STOP_RES: readonly RegExp[] = [
  new RegExp(String.raw`\b(?:to\s+)?(?:unsubscribe|opt[- ]?out)\b[^.!?\n]{0,30}?[?,:-]?\s*(?:reply|text|txt|send)\s+${CANNED_KW}(?![\w])[^.!?\n]*`, 'gi'),
  new RegExp(String.raw`\bto\s+stop(?:\s+(?:receiving|getting)\b[^.!?\n]{0,40}?)?\s*[,:-]?\s*(?:reply|text|txt|send)\s+${CANNED_KW}(?![\w])[^.!?\n]*`, 'gi'),
  new RegExp(String.raw`\b(?:reply|text|txt|send|respond)(?:\s+with)?\s+${CANNED_KW}${CANNED_ANYTIME}\s+(?:to|2|for)\b[^.!?\n]*`, 'gi'),
  new RegExp(String.raw`(?<![\w'])${CANNED_KW}${CANNED_ANYTIME}\s+(?:to|2)\s+(?:opt[- ]?out|unsubscribe|unsub|cancel|end|stop|quit)\b[^.!?\n]*`, 'gi'),
  /\b(?:reply|text|txt|send)\s+["']?(?:STOP|END|QUIT|UNSUBSCRIBE)["']?\s*(?=[.!)]|$)/g,
  // ship check of 09da0c7: "Unsubscribe anytime by replying STOP", "You can unsubscribe at any time by texting STOP",
  // "You may unsubscribe at any time", "Msg&data rates may apply. Unsubscribe with STOP", "Unsubscribe by texting STOP",
  // "Reply UNSUBSCRIBE at any time"
  /\b(?:you can |you may |to )?unsub(?:scribe)?(?: at)? any ?time\b[^.!?\n]*/gi,
  /\bunsub(?:scribe)? (?:by|with) (?:replying|texting|reply|text|sending|send)?\s*["']?(?:stop|unsubscribe|end|quit|cancel)["']?[^.!?\n]*/gi,
  /\breply\s+["']?unsubscribe["']?(?:\s+(?:at\s+)?any\s?time)?[^.!?\n]*/gi,
];
export const smsWithoutCannedStop = (s: string): string =>
  CANNED_STOP_RES.reduce((t, re) => t.replace(re, ' '), s.replace(/[’‘`]/g, "'").replace(/[“”]/g, '"'));
/** A person asking how to stop ("How do I unsubscribe, reply stop?", "I want to opt out"): read before the canned lines
 *  are taken out, since it can look like one. */
// Ship check of 09da0c7: only a person (I / we): "If you'd like to unsubscribe, reply STOP" and "Want to stop receiving
// texts? Reply STOP" are a business's auto text.
const ASKS_TO_UNSUBSCRIBE_RE = /\b(?:how (?:do|can|would) (?:i|we) |(?:i|we|i'?d|we'?d) (?:want to|wanna|need to|would like to|like to) |\bi'?d like to |let me |can (?:i|we) |(?:please|pls|plz) )(?:unsubscribe|unsub|opt[- ]?out|stop (?:these|the|your|getting|receiving) (?:texts?|messages?|msgs))\b/i;
/** Text as bare words: lower case, accents and punctuation (emoji too) gone. "Stop 🛑" -> "stop", "Arrêt!" -> "arret". */
const bareWords = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  .replace(/[^a-z0-9' ]+/g, ' ').replace(/\s+/g, ' ').trim();
/** Only stop keywords, maybe repeated, with a please / thanks ("stop stop stop", "Stop?", "STOP 🙏", "cancel please",
 *  "Stop all", "unsub", "Stop. Thanks"). */
const KEYWORD_ONLY_RE = /^(?:(?:please|pls|plz|ok|okay|just) )?(?:(?:stop ?all|stop|unsubscribe|unsub|opt ?out|revoke|arret|cancel|end|quit)(?: |$))+(?:(?:please|pls|plz|now|it|already|thanks|thank you|thx|ty|asap)(?: |$))*$/;
/** A clause that is only a stop keyword ("Who is this? STOP", "STOP. Who is this", "Stop, wrong number", "I said stop").
 *  Cancel, end and quit only as the whole text: "Can't make it. Cancel." is about a meeting. */
const CLAUSE_KEYWORD_RE = /^(?:(?:please|pls|plz|ok|okay|just|i said|i already said) )?(?:stop ?all|stop|unsubscribe|unsub|opt ?out|revoke|arret)(?: (?:please|pls|plz|now|it|already|thanks|thank you|thx|ty|asap))*$/;
const JOKING_RE = /\b(?:lol|lmao|haha\w*|jk|hilarious|kidding|joking|funny)\b/i;
function stopKeyword(t: string): boolean {
  if (KEYWORD_ONLY_RE.test(bareWords(t))) return true;
  if (JOKING_RE.test(t)) return false;             // "Stop, that's hilarious"
  // clauses split at punctuation and at a dash between words ("Stop - wrong number")
  return t.split(/[.!?;,\n]+|\s+[-–—]+\s+|\s*[–—]+\s*/).some((c) => CLAUSE_KEYWORD_RE.test(bareWords(c)));
}
/** A plain request to stop in their own words (not the exact keyword): a stop keyword standing on its own, a request to
 *  stop texting or all contact, or to stop calling without asking for another channel, number or time. */
export function smsStopPhrase(body: string | null | undefined): boolean {
  const raw = String(body ?? '').replace(/[’‘`]/g, "'");
  if (ASKS_TO_UNSUBSCRIBE_RE.test(raw)) return true;
  const t = smsWithoutCannedStop(raw);
  if (stopKeyword(t)) return true;
  if (STOP_TEXTS_RES.some((re) => re.test(t))) return true;
  return STOP_CALLS_RES.some((re) => re.test(t)) && !CALLS_REDIRECT_RE.test(t);
}
/** True when an inbound text asks us to stop: exactly a stop word, or a plain request in their own words. */
export function smsAsksToStop(body: string | null | undefined): boolean {
  const w = (body ?? '').trim().toUpperCase().replace(/[\s.!]+$/, '');
  if (SMS_STOP_WORDS.includes(w) || w === 'OPT OUT' || w === 'OPT-OUT') return true;
  return smsStopPhrase(body);
}
/** The consent tick on the first text to a number that has never texted us (stored once per conversation). */
export const SMS_CONSENT_LABEL = "They gave me this number and said it's OK to text it";
/** Shown on US conversations while setting sms_us_a2p_approved is false. */
export const SMS_US_PENDING_NOTE = "US texts won't deliver until carriers approve our registration (pending).";
/** The hard blocks that never lift (routes/sms.ts sendBlock), worded once for the server and the tests. */
export const SMS_BLOCK_DISPENSARY = "We don't text dispensaries (Twilio bans cannabis messaging). Call or email instead.";
export const SMS_BLOCK_OPTED_OUT = "They replied STOP, so we don't text them.";

export interface SmsThreadItem {
  id: number;
  business: { id: number; name: string } | null;   // null = unmatched
  theirE164: string;
  ourE164: string;
  ourNumberId: number | null;
  assignedUserId: number | null;                    // null = any admin
  assignedUserName: string | null;
  linkSource: SmsLinkSource | null;
  lastPreview: string;                              // last message, trimmed ('(picture)' for a picture with no text)
  lastDirection: SmsDirection | null;
  lastMessageAt: ISODate;
  unread: number;                                   // inbound messages not yet read by the person the thread is for
  /** Their number replied STOP (sms_optouts): the "Replied STOP" chip; nothing can be sent to it. */
  optedOut: boolean;
  /** No text from them in this conversation and nobody ticked consent yet: the next send needs `consent: true`. */
  consentNeeded: boolean;
}

export interface SmsMessage {
  id: number;
  threadId: number;
  direction: SmsDirection;
  body: string;
  mediaCount: number;
  status: SmsStatus;
  errorCode: number | null;
  errorText: string | null;                         // plain English, e.g. the 30034 / 21610 sentences
  userId: number | null;                            // out: who sent it
  userName: string | null;
  lateConfirmed: boolean;
  createdAt: ISODate;
  readAt: ISODate | null;
}

export interface SmsThreadDetail {
  thread: SmsThreadItem;
  messages: SmsMessage[];                           // oldest first (last 500)
  /** The recipient's country (the business's, else the area code's): the composer's Canada opt-out hint. */
  country: Country | null;
  /** The Calendly event slug of the business's campaign, for "Insert booking link". null = no campaign. */
  calendlySlug: string | null;
  /** Why nothing can be sent in this thread (do-not-call, do-not-contact, STOP, dispensary, a closed state when they
   *  never texted us, our number retired). null = can send. */
  sendBlocked: string | null;
  canLink: boolean;
  canAssign: boolean;
  /** Unmatched: the businesses it could belong to (one-click "Link to ..."), only those the viewer can open. */
  linkCandidates: Array<{ id: number; name: string }>;
  /** Setting sms_us_a2p_approved: false = US texts are still pending carrier approval (the composer says so). */
  usA2pApproved: boolean;
}

export interface SmsTextableNumber {
  e164: string;
  label: string;                                    // 'Business line', 'Texted us', 'Mobile from a booking', ...
  blocked: string | null;                           // e.g. 'On the do-not-call list', 'Replied STOP'
}

export interface SmsBusinessView {
  business: { id: number; name: string; country: Country; tz: string; phoneE164: string };
  threads: SmsThreadItem[];                         // every conversation with this business, newest first
  /** The number a NEW conversation would come from (sticky caller ID, else best same-country number, CA: main line). */
  suggestedFrom: { id: number; e164: string; why: string } | null;
  fromProblem: string | null;                       // set when no number of ours can text this business
  textable: SmsTextableNumber[];
  calendlySlug: string | null;
  /** Why nothing can be texted to this business at all (do-not-contact, dispensary; or a calling block such as a closed
   *  state, Quebec, Canadian healthcare or the National DNC when none of its numbers ever texted us). The call screen's
   *  Text button is disabled with this sentence. null = something can be sent (see textable[].blocked per number). */
  blocked: string | null;
  /** A fact worth knowing before sending, e.g. the last text from a US number failed with 30034 (A2P pending). */
  warning: string | null;
  usA2pApproved: boolean;
}

export interface SmsUnread {
  count: number;
  /** The newest unread text: drives the "New text from …" toast when the count rises. */
  latest: { threadId: number; messageId: number; from: string; at: ISODate } | null;
}

export interface SmsSendRequest {
  threadId?: number;                                // reply in this thread
  businessId?: number;                              // or text a business (the call screen's panel; New text linked to a lead)
  /** With businessId: which of their numbers (default: the business line). Alone (New text, owner request 2026-10-04):
   *  any number someone gave us; it goes into a conversation with that number the sender can see, else a new one. */
  to?: string;
  body: string;                                     // 1-1600 characters
  confirmLate?: boolean;                            // true after the setter said "Send anyway" to the 409 late_hour
  /** The consent tick: required (409 consent_needed) on the first text to a number that never texted us. */
  consent?: boolean;
}

export interface SmsSendResponse {
  ok: boolean;                                      // false: Twilio refused it; message.status is 'failed' with errorText.
                                                    // true with status 'queued' + errorText: Twilio didn't answer in time
                                                    // (unconfirmed; the status callback settles it)
  threadId: number;
  message: SmsMessage;
  /** true = this text started a new conversation; false = it went into one that already existed. */
  newConversation: boolean;
}

/** GET /api/sms/new-text?to=&businessId= : what the New text composer needs for one typed number before sending. The
 *  same checks as POST /send, except the per-user cap and the 8 AM-9 PM question (those come at send time). */
export interface SmsNewTextView {
  to: string;                                       // E.164
  /** A conversation with this number the viewer can see: the text goes into it (never a second one). null = new. */
  existingThreadId: number | null;
  /** The lead it is filed under: the one picked, or the lead whose number this is (that lead's rules then apply). */
  business: { id: number; name: string } | null;
  /** The number it goes out from, and why. null when a block stopped it before a number was picked. */
  from: { id: number; e164: string; why: string } | null;
  country: Country | null;
  /** Why it can't be sent, in one plain sentence. null = can send. */
  blocked: string | null;
  /** The consent tick is needed (they never texted us in that conversation and nobody ticked it yet). */
  consentNeeded: boolean;
  /** The Calendly event of the lead's campaign, for "Insert booking link". null = no lead with a campaign. */
  calendlySlug: string | null;
  usA2pApproved: boolean;
}

/** 409 late_hour details: their local clock and zone. */
export interface SmsLateHourDetails { localTime: string; tz: string }

// ---------- AI help (owner request, PJ, 2026-10-06) ----------
// "AI draft" writes the next text from the conversation, the lead and our calls with them (notes and the call
// transcript); the setter edits it and sends it as normal. "Summarize" says where the conversation stands and one next
// step. Neither ever sends anything (src/worker/ai/smsDraft.ts).

export type SmsAssistMode = 'draft' | 'summary';
/** "Tell the AI what to say" (e.g. "offer Thursday 2pm"): one line, this many characters at most. */
export const SMS_ASSIST_INSTRUCTIONS_MAX = 300;
/** An AI draft is one short text: at most this many characters (opt-out line included). */
export const SMS_ASSIST_DRAFT_MAX_CHARS = 320;
/** AI drafts + summaries per user per 24 hours (429 rate_limited above it). */
export const SMS_ASSISTS_PER_DAY = 100;

export interface SmsAssistRequest {
  mode: SmsAssistMode;
  /** Draft only: what the text should do, in the setter's words. Up to SMS_ASSIST_INSTRUCTIONS_MAX characters. */
  instructions?: string;
  /** /business/:id/assist only: which of their numbers the text is for (default: the business line). */
  to?: string;
}

export interface SmsAssistDraft {
  mode: 'draft';
  /** The next text, plain, at most SMS_ASSIST_DRAFT_MAX_CHARS characters. Not sent: it goes in the message box. */
  draft: string;
  /** What it was based on, e.g. "Based on 4 texts and 2 calls (with the call recording)." */
  basis: string;
}

export interface SmsAssistSummary {
  mode: 'summary';
  /** 1 to 3 short lines: what they want, where it stands. */
  summary: string;
  /** One suggested next step for the setter. */
  nextStep: string;
  basis: string;
}

export type SmsAssistResponse = SmsAssistDraft | SmsAssistSummary;

// ---------- segments (pure; the composer's counter) ----------

// GSM 03.38 basic set (1 unit each) and extension set (2 units each: escape + char). Anything else forces UCS-2.
const GSM_BASIC = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM_EXT = '^{}\\[~]|€\f';

export interface SmsSegments { chars: number; segments: number; encoding: 'GSM-7' | 'UCS-2'; perSegment: number }

/** How many SMS segments `text` costs: GSM-7 is 160 per single text, 153 per part when split; UCS-2 (any emoji or
 *  character outside GSM) is 70, then 67. `chars` counts characters as the carrier does (UTF-16 units for UCS-2). */
export function smsSegments(text: string): SmsSegments {
  let units = 0;
  let gsm = true;
  for (const ch of text) {
    if (GSM_BASIC.includes(ch)) units += 1;
    else if (GSM_EXT.includes(ch)) units += 2;
    else { gsm = false; break; }
  }
  if (gsm) {
    const segments = units === 0 ? 0 : units <= 160 ? 1 : Math.ceil(units / 153);
    return { chars: units, segments, encoding: 'GSM-7', perSegment: units <= 160 ? 160 : 153 };
  }
  const len = text.length; // UTF-16 code units: an emoji counts 2
  const segments = len <= 70 ? 1 : Math.ceil(len / 67);
  return { chars: len, segments, encoding: 'UCS-2', perSegment: len <= 70 ? 70 : 67 };
}
